import { CartAddEvent, CartErrorEvent } from './events.js';
import { cartPerformance } from './performance-component.js';

const selectors = {
  cartSection: '[data-cart-drawer-section], [data-cart-page-section]',
  productForm: 'form[data-product-form][action*="/cart/add"]',
  submit: '[data-product-submit]',
};

const routesRoot = window.Shopify?.routes?.root || '/';

/**
 * @param {'cartAdding' | 'cartError' | 'cartVerificationError'} key
 * @returns {string}
 */
function localizedCartText(key) {
  return document.documentElement.dataset[key] || '';
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll(selectors.cartSection))
    .map((section) => section.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);
}

function appendCartSections(formData) {
  const sectionIds = cartSectionIds();
  if (sectionIds.length === 0) return;

  const sectionsUrl = new URL(window.location.href);
  sectionsUrl.searchParams.delete('sections');
  sectionsUrl.searchParams.delete('section_id');

  formData.append('sections', sectionIds.join(','));
  formData.append('sections_url', `${sectionsUrl.pathname}${sectionsUrl.search}`);
}

function getFormSubmitButton(form) {
  return form.querySelector(selectors.submit) || form.querySelector('button[type="submit"]');
}

function setProductFormBusy(form, busy) {
  const button = getFormSubmitButton(form);
  form.toggleAttribute('aria-busy', busy);
  if (!button) return;

  if (busy) {
    button.dataset.originalText = button.innerHTML;
    button.dataset.busyWasDisabled = button.disabled ? 'true' : 'false';
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('data-cart-pending', 'true');
    button.textContent = button.dataset.loadingLabel || localizedCartText('cartAdding');
    return;
  }

  const shouldStayDisabled =
    button.dataset.busyWasDisabled === 'true' ||
    button.dataset.variantAvailable === 'false' ||
    button.dataset.purchaseOptionValid === 'false';
  button.disabled = shouldStayDisabled;
  if (shouldStayDisabled) {
    button.setAttribute('aria-disabled', 'true');
  } else {
    button.removeAttribute('aria-disabled');
  }

  button.removeAttribute('data-cart-pending');
  if (button.dataset.originalText) {
    button.innerHTML = button.dataset.originalText;
    delete button.dataset.originalText;
  }
  delete button.dataset.busyWasDisabled;
}

function setProductFormAdded(form) {
  const button = getFormSubmitButton(form);
  if (!button) return;

  button.setAttribute('data-cart-added', 'true');
  window.setTimeout(() => button.removeAttribute('data-cart-added'), 900);
}

function setProductFormError(form, message) {
  let error = form.querySelector('[data-product-form-error]');
  if (!message) {
    error?.remove();
    return;
  }

  if (!error) {
    error = document.createElement('p');
    error.className = 'md-form-error';
    error.setAttribute('role', 'alert');
    error.dataset.productFormError = '';
    form.append(error);
  }

  error.textContent = message;
}

async function cartErrorFromResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const cartError = new Error(localizedCartText('cartError'));
  cartError.cartStatus = response.status;

  if (contentType.includes('application/json')) {
    try {
      const error = await response.json();
      cartError.message = error.description || error.message || cartError.message;
      cartError.errors = error.errors || null;
    } catch (error) {
      /* Keep the generic message when Shopify returns malformed JSON. */
    }
    return cartError;
  }

  if (contentType.includes('text/html')) {
    const body = await response.text().catch(() => '');
    const verificationPage = /verifying your connection/i.test(body);
    if (verificationPage || response.status === 401 || response.status === 403) {
      cartError.message = localizedCartText('cartVerificationError');
    }
  }

  return cartError;
}

function sourceIdFor(form) {
  return form.id || form.dataset.productFormScope || form.closest('[id]')?.id || 'product-form';
}

export async function addToCart(form, event = null) {
  if (form.dataset.ajaxBusy === 'true') return;

  cartPerformance.measureFromEvent('add:user-action', event || { timeStamp: performance.now() });

  form.dataset.ajaxBusy = 'true';
  setProductFormError(form, '');
  setProductFormBusy(form, true);

  const formData = new FormData(form);
  appendCartSections(formData);
  const sourceId = sourceIdFor(form);
  const requestMarker = cartPerformance.createStartingMarker('add:request');

  try {
    const response = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    if (!response.ok) throw await cartErrorFromResponse(response);

    const result = await response.json();
    cartPerformance.measureFromMarker(requestMarker);
    setProductFormAdded(form);

    document.dispatchEvent(
      new CartAddEvent(result, sourceId, {
        sections: result.sections || {},
        itemCount: result.item_count,
        variantId: formData.get('id'),
        form,
        returnFocus: getFormSubmitButton(form) || form,
      }),
    );
  } catch (error) {
    const message = error.message || localizedCartText('cartError');
    setProductFormError(form, message);
    document.dispatchEvent(new CartErrorEvent(sourceId, message, error.description, error.errors));
    throw error;
  } finally {
    form.dataset.ajaxBusy = 'false';
    setProductFormBusy(form, false);
  }
}

document.addEventListener('submit', (event) => {
  if (!(event.target instanceof Element)) return;

  const form = event.target.closest(selectors.productForm);
  if (!form) return;

  event.preventDefault();
  addToCart(form, event).catch(() => {});
});

window.MedistikProductForm = {
  addToCart,
};
